import type BaseModel from "../../../src/models/BaseModel";
import type Room from "../../../src/models/Room";
import { C1_Furniture } from "./C1_Furniture";
import { C1_EloGroup } from "./C1_EloGroup";
import { C1_Consumer } from "./C1_Consumer";
import { C1_Button } from "./C1_Button";
import type { C1_Room } from "./C1_Room";

/**
 * Whether a navigator tree node is a C1 object independently placed in its room (furniture such as a
 * sofa, or a standalone electrical device/group), as opposed to equipment that belongs to a room's or
 * furniture's definition-driven tree (e.g. a repeated socket inside a wardrobe). Only independently
 * placed objects have their own room-relative placement and can be dragged to a different room.
 */
export function isIndependentlyPlacedC1Object(obj: BaseModel): boolean {
  const facet = obj.c1;
  if (facet instanceof C1_Furniture) return true;
  if (facet instanceof C1_Consumer || facet instanceof C1_Button || facet instanceof C1_EloGroup) {
    return facet.interactivCreated === true;
  }
  return false;
}

/**
 * Repositions a C1 object at the target room's clearance point after the navigator already moved it
 * there (e.g. by dragging a furniture or device node onto a different room in the tree). Unlike dragging
 * within the 2D plan, a tree move has no pointer position to anchor the new placement, so the object is
 * centered in the new room and redocked to the nearest wall only if it lands close enough to one.
 * No-ops for non-C1 projects and for objects without an independent placement.
 */
export function repositionAfterRoomMove(obj: BaseModel, room: Room): void {
  const c1Room = room.c1 as C1_Room | undefined;
  if (!c1Room) return;
  const facet = obj.c1;
  if (facet instanceof C1_Furniture) {
    facet.moveTo(c1Room.automaticPlacementPoint());
    return;
  }
  if ((facet instanceof C1_Consumer || facet instanceof C1_Button || facet instanceof C1_EloGroup) && facet.interactivCreated) {
    facet.moveInteractiveTo(c1Room.automaticPlacementPoint());
  }
}
